import { FC } from 'react';

import { MousePointer2 } from 'lucide-react';

import Typography from 'components/Typography/Typography';

import { AnalyticsInfoTooltip } from '../../AnalyticsInfoTooltip/AnalyticsInfoTooltip';
import {
  IAnalyticsStackedTimeSeriesPrimaryMetric,
  IAnalyticsStackedTimeSeriesSegment
} from '../AnalyticsStackedTimeSeriesBox.types';
import {
  formatSegmentSummary,
  getPrimaryMetricDisplayValue
} from '../AnalyticsStackedTimeSeriesBox.utils';

import { AnalyticsStackedTimeSeriesSegmentSwatch } from './AnalyticsStackedTimeSeriesSegmentSwatch';

interface IAnalyticsStackedTimeSeriesHeaderProps {
  title: string;
  tooltipInfo?: string;
  primaryMetric: IAnalyticsStackedTimeSeriesPrimaryMetric;
  segments: IAnalyticsStackedTimeSeriesSegment[];
  valueFormatter: (value: number) => string;
}

export const AnalyticsStackedTimeSeriesHeader: FC<IAnalyticsStackedTimeSeriesHeaderProps> = ({
  title,
  tooltipInfo,
  primaryMetric,
  segments,
  valueFormatter
}) => {
  return (
    <div className="flex flex-col w-full">
      <div className="flex items-center px-6 py-2.5 border-b border-border gap-1.5 w-full">
        <MousePointer2 size={16} className="text-base-mutedForeground" />
        <Typography
          data-testid="analytics-stacked-time-series-box-title"
          className="text-base-mutedForeground text-sm leading-5"
        >
          {title}
        </Typography>
      </div>

      <div className="flex p-6 gap-6 w-full">
        <div className="flex flex-col border-r border-border pr-6 w-[258px] shrink-0">
          <div className="flex items-center gap-1.5 mb-1.5">
            <Typography className="text-base-cardForeground font-semibold text-base leading-none tracking-tight">
              {primaryMetric.label}
            </Typography>
            {tooltipInfo && (
              <AnalyticsInfoTooltip>{tooltipInfo}</AnalyticsInfoTooltip>
            )}
          </div>

          <div className="flex items-end gap-3.5">
            <span
              data-testid="analytics-stacked-time-series-box-primary-value"
              className="font-semibold text-[36px] leading-[40px] tracking-tight text-base-cardForeground"
            >
              {getPrimaryMetricDisplayValue(primaryMetric.value, valueFormatter)}
            </span>
            {primaryMetric.statisticLabel && (
              <span className="text-base-mutedForeground text-sm leading-5 mb-1">
                {primaryMetric.statisticLabel}
              </span>
            )}
          </div>
        </div>

        <div className="flex flex-col justify-center gap-2 flex-1 min-w-0">
          {segments.map((segment) => (
            <div key={segment.key} className="flex items-center gap-1.5">
              <AnalyticsStackedTimeSeriesSegmentSwatch color={segment.color} />
              <span className="text-base-cardForeground text-base font-semibold leading-none tracking-tight truncate">
                {formatSegmentSummary(segment, valueFormatter)}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
